// New ASO.Market order: what to buy, for which app, where and how fast, with
// the price quoted live on the right before anything is spent.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { useToastStore } from '@/store/toast';
import {
  checkRank, generateReviewTexts, getApps, placeOrder, quoteOrder,
  type AsomApp, type AsomOrderInput, type AsomOverview, type AsomQuote,
} from '@/api/asoMarket';
import {
  AppLogo, FieldLabel, INPUT, Notice, Segmented, Stars, Toggle, flag, hoursText, usd,
} from './ui';

type Kind = 'ratings' | 'reviews' | 'installs';
type Unit = 'min' | 'h';

const HOLD_LABEL: Record<string, string> = {
  unreleased: 'not released yet', release: 'release hold', meta: 'metadata hold', build: 'update hold', spike: 'install-spike hold', ramp: 'offer ramp-up',
};

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function OrderModal({ open, onClose, overview, initialAppId }: {
  open: boolean; onClose: () => void; overview: AsomOverview; initialAppId?: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const apps = useQuery({ queryKey: ['aso-market', 'apps'], queryFn: getApps, enabled: open, staleTime: 60_000 });
  const constraints = overview.constraints;
  const minInterval = constraints?.minIntervalMinutes ?? 10;
  const minTotal = constraints?.minReviewsNumber ?? 2;

  const [appId, setAppId] = useState<string | null>(initialAppId ?? null);
  const [appSearch, setAppSearch] = useState('');
  const [external, setExternal] = useState('');
  const [useExternal, setUseExternal] = useState(false);
  const [kind, setKind] = useState<Kind>('ratings');
  const [rating, setRating] = useState(5);
  const [search, setSearch] = useState<0 | 1 | 2>(0);
  const [keyword, setKeyword] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [total, setTotal] = useState('10');
  const [interval, setIntervalValue] = useState('60');
  const [unit, setUnit] = useState<Unit>('min');
  const [texts, setTexts] = useState('');
  const [highRisk, setHighRisk] = useState(false);
  const [noGuarantee, setNoGuarantee] = useState(false);
  const [screens, setScreens] = useState(false);
  const [likes, setLikes] = useState(false);
  const [share, setShare] = useState('');
  const [promo, setPromo] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [genLang, setGenLang] = useState('');
  const [genNotes, setGenNotes] = useState('');
  const [rank, setRank] = useState<{ country: string; value: number | null } | null>(null);

  useEffect(() => { if (open && initialAppId) { setAppId(initialAppId); setUseExternal(false); } }, [open, initialAppId]);

  const app: AsomApp | null = useMemo(
    () => (useExternal ? null : apps.data?.apps.find((a) => a.id === appId) ?? null),
    [apps.data, appId, useExternal],
  );
  const pkg = useExternal ? external.trim() : app?.bundle_id ?? '';
  const textList = useMemo(() => texts.split('\n').map((t) => t.trim()).filter(Boolean), [texts]);
  const totalN = Math.floor(Number(total) || 0);
  const intervalHours = (Number(interval) || 0) / (unit === 'min' ? 60 : 1);
  const countryInfo = useMemo(() => new Map((overview.countries ?? []).map((c) => [c.code, c])), [overview.countries]);

  const order: AsomOrderInput = {
    app_id: app?.id ?? null,
    package_name: pkg,
    type: kind === 'installs' ? 3 : 1,
    countries,
    total: totalN,
    interval_hours: intervalHours,
    rating: kind === 'installs' ? 0 : rating,
    add_text: kind === 'reviews',
    review_texts: kind === 'reviews' ? textList : [],
    search_keyword_type: search,
    search_keyword: search === 2 ? keyword.trim() : null,
    no_guarantee: noGuarantee,
    require_in_app_screenshots: screens,
    is_high_risk: highRisk,
    add_likes: kind !== 'installs' && likes,
    tasks_per_country_share_limit: countries.length > 1 && share ? Number(share) : null,
    promo_code: promo.trim() || null,
  };

  // Texts do not change the price: leave them out of the quote key.
  const quoteKey = JSON.stringify({ ...order, review_texts: [] });
  const debouncedKey = useDebounced(quoteKey, 500);
  const quotable = !!pkg && countries.length > 0 && totalN >= minTotal && intervalHours * 60 >= minInterval
    && (search !== 2 || !!keyword.trim());
  const quote = useQuery({
    queryKey: ['aso-market', 'quote', debouncedKey],
    // The server checks the text count even for a quote: stand-ins keep it happy.
    queryFn: () => {
      const o = JSON.parse(debouncedKey) as AsomOrderInput;
      return quoteOrder(o.add_text ? { ...o, review_texts: Array(o.total).fill('·') } : o);
    },
    enabled: open && quotable && debouncedKey === quoteKey,
    retry: false,
    staleTime: 30_000,
  });
  const q: AsomQuote | undefined = quote.data;

  const generate = useMutation({
    mutationFn: () => generateReviewTexts({
      app_id: app?.id ?? null, package_name: pkg, count: Math.max(0, totalN - textList.length) || totalN,
      rating, language: genLang || countryInfo.get(countries[0] ?? '')?.language || 'en', notes: genNotes,
    }),
    onSuccess: (d) => setTexts((prev) => [...prev.split('\n').filter((t) => t.trim()), ...d.texts].join('\n')),
    onError: (e: Error) => toast(e.message),
  });

  const rankCheck = useMutation({
    mutationFn: (country: string) => checkRank(pkg, country, keyword.trim()),
    onSuccess: (d, country) => setRank({ country, value: d.rank }),
    onError: (e: Error) => toast(e.message),
  });

  const submit = useMutation({
    mutationFn: () => placeOrder(order),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['aso-market'] });
      if (d.state === 'unknown') toast(d.message ?? 'ASO.Market did not answer; the sync will find the campaign.');
      else toast(d.status === 10 ? 'Campaign created — it waits for a top-up' : 'Campaign created and running');
      onClose();
    },
    onError: (e: Error) => toast(e.message),
  });

  // Ready-to-read warnings next to the price.
  const warnings: { tone: 'warning' | 'danger' | 'info'; text: string }[] = [];
  if (app?.is_banned) warnings.push({ tone: 'danger', text: 'This app is marked banned: performers will not find it.' });
  else if (app && !app.in_store) warnings.push({ tone: 'warning', text: 'This app is not seen in the store yet.' });
  const forbidden = app ? countries.filter((c) => app.forbidden_countries.includes(c)) : [];
  if (forbidden.length) warnings.push({ tone: 'warning', text: `Forbidden geo for this app: ${forbidden.join(', ')}.` });
  const spike = apps.data?.spike;
  const perHour = intervalHours > 0 ? 1 / intervalHours : 0;
  if (kind === 'installs' && spike?.enabled && perHour >= spike.min_devices / 2) {
    warnings.push({ tone: 'warning', text: `≈${Math.round(perHour)} installs/hour can trip the install-spike detector (≥${spike.min_devices} new devices/hour) and hold the force offer for ${spike.hours} h. A longer interval avoids it.` });
  }
  if (app?.hold && app.hold.kind !== 'unreleased') {
    warnings.push({ tone: 'info', text: `Force offer is on ${HOLD_LABEL[app.hold.kind] ?? app.hold.kind}${app.hold.until ? ` until ${new Date(app.hold.until).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}: performers see the store app.` });
  }
  if (q && q.cost > q.balance) warnings.push({ tone: 'warning', text: `Balance ${usd(q.balance)} is short by ${usd(q.cost - q.balance)}: the campaign is created but waits for a top-up.` });
  if (q?.over_cap && !q.can_exceed_cap) warnings.push({ tone: 'danger', text: `Over the ${usd(q.max_cost, 0)} order cap: a super admin has to place it.` });

  const textsShort = kind === 'reviews' && textList.length < totalN;
  const canSubmit = quotable && !!q && !(q.over_cap && !q.can_exceed_cap) && !textsShort && !submit.isPending;
  const filteredApps = (apps.data?.apps ?? []).filter((a) => {
    const s = appSearch.trim().toLowerCase();
    return !s || a.name.toLowerCase().includes(s) || a.bundle_id.toLowerCase().includes(s);
  });
  const shareMin = q?.share_min ?? null;

  return (
    <Modal isOpen={open} onClose={onClose} title="New ASO.Market order" size="5xl">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6 min-w-0">
          {/* App */}
          <section>
            <FieldLabel aside={apps.data?.unrestricted ? (
              <button type="button" className="text-primary hover:underline" onClick={() => setUseExternal(!useExternal)}>
                {useExternal ? 'Pick from our apps' : 'Other app by link'}
              </button>
            ) : undefined}>App</FieldLabel>
            {useExternal ? (
              <input className={INPUT} placeholder="https://play.google.com/store/apps/details?id=… or package name" value={external} onChange={(e) => setExternal(e.target.value)} />
            ) : app ? (
              <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <AppLogo src={app.icon_url} name={app.name} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-content truncate">{app.name}</div>
                  <div className="text-xs text-content-subtle font-mono truncate">{app.bundle_id}</div>
                </div>
                <Button size="sm" variant="secondary" onClick={() => setAppId(null)}>Change</Button>
              </div>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <input className="block w-full px-3 py-2 text-sm bg-surface text-content border-b border-border focus:outline-none" placeholder="Search our Android apps…" value={appSearch} onChange={(e) => setAppSearch(e.target.value)} autoFocus />
                <div className="max-h-56 overflow-y-auto divide-y divide-border">
                  {apps.isLoading && <p className="p-3 text-sm text-content-subtle">Loading apps…</p>}
                  {filteredApps.slice(0, 80).map((a) => (
                    <button key={a.id} type="button" onClick={() => { setAppId(a.id); setAppSearch(''); }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-surface-muted">
                      <AppLogo src={a.icon_url} name={a.name} size={28} />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-content truncate">{a.name}</span>
                        <span className="block text-xs text-content-subtle font-mono truncate">{a.bundle_id}</span>
                      </span>
                      {a.is_banned && <span className="text-xs text-danger">banned</span>}
                      {!a.is_banned && !a.in_store && <span className="text-xs text-content-subtle">not live</span>}
                    </button>
                  ))}
                  {apps.data && filteredApps.length === 0 && <p className="p-3 text-sm text-content-subtle">No apps match.</p>}
                </div>
              </div>
            )}
          </section>

          {/* What */}
          <section>
            <FieldLabel>What to buy</FieldLabel>
            <div className="grid grid-cols-3 gap-2">
              {([
                ['ratings', '★', 'Ratings', 'Stars only'],
                ['reviews', '✍︎', 'Reviews', 'Stars + your texts'],
                ['installs', '⤓', 'Installs', 'Install and open'],
              ] as const).map(([k, icon, title, sub]) => (
                <button key={k} type="button" onClick={() => setKind(k)}
                  className={`p-3 rounded-lg border text-left transition-colors ${kind === k ? 'border-primary bg-primary-50 dark:bg-primary-900/30' : 'border-border hover:border-border-strong'}`}>
                  <div className="text-lg leading-none">{icon}</div>
                  <div className="mt-1.5 text-sm font-medium text-content">{title}</div>
                  <div className="text-xs text-content-subtle">{sub}</div>
                </button>
              ))}
            </div>
            {kind !== 'installs' && (
              <div className="mt-3 flex items-center gap-3">
                <Stars value={rating} onChange={setRating} />
                <span className="text-sm text-content-muted">{rating} star{rating > 1 ? 's' : ''}</span>
              </div>
            )}
          </section>

          {/* How they find it */}
          <section>
            <FieldLabel>How performers find the app</FieldLabel>
            <Segmented value={search} onChange={(v) => { setSearch(v); setRank(null); }} options={[
              { value: 0, label: 'Direct link', hint: 'Cheapest: opens the store page' },
              { value: 1, label: 'Search by name', hint: 'Types the app name in Play search' },
              { value: 2, label: 'Search by keyword', hint: 'Types your keyword and scrolls to the app' },
            ]} />
            {search === 2 && (
              <div className="mt-3 space-y-2">
                <input className={INPUT} placeholder="Keyword, e.g. plinko game" maxLength={constraints?.maxSearchKeywordLength ?? 200} value={keyword} onChange={(e) => { setKeyword(e.target.value); setRank(null); }} />
                {pkg && keyword.trim() && countries.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-content-muted">
                    <span>Current position:</span>
                    {countries.slice(0, 6).map((c) => (
                      <button key={c} type="button" disabled={rankCheck.isPending} onClick={() => rankCheck.mutate(c)}
                        className="px-2 py-1 rounded border border-border hover:border-border-strong">
                        {flag(c)} {c}{rank?.country === c ? `: ${rank.value ? `#${rank.value}` : 'not found'}` : ''}
                      </button>
                    ))}
                    {rankCheck.isPending && <span>checking…</span>}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Where */}
          <section>
            <FieldLabel aside={`${overview.countries?.length ?? 0} countries available`}>Countries</FieldLabel>
            <CountryMultiSelect value={countries} onChange={setCountries} restrictTo={(overview.countries ?? []).map((c) => c.code)} placeholder="Search countries ASO.Market sells…" />
          </section>

          {/* How many, how fast */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel aside={`min ${minTotal}`}>Tasks</FieldLabel>
              <input type="number" min={minTotal} className={INPUT} value={total} onChange={(e) => setTotal(e.target.value)} />
            </div>
            <div>
              <FieldLabel aside={`min ${minInterval} min`}>One task every</FieldLabel>
              <div className="flex items-center gap-2">
                <input type="number" min={0} step="any" className={INPUT} value={interval} onChange={(e) => setIntervalValue(e.target.value)} />
                <Segmented value={unit} onChange={(u) => {
                  if (u === unit) return;
                  setIntervalValue(String(u === 'h' ? Math.round((Number(interval) / 60) * 100) / 100 : Math.round(Number(interval) * 60)));
                  setUnit(u);
                }} nowrap options={[{ value: 'min', label: 'min' }, { value: 'h', label: 'h' }]} />
              </div>
            </div>
            {totalN > 0 && intervalHours > 0 && (
              <p className="sm:col-span-2 text-xs text-content-subtle -mt-2">
                ≈ {perHour >= 1 ? `${Math.round(perHour * 10) / 10} per hour` : `1 every ${hoursText(intervalHours)}`} · the whole order takes about {hoursText(totalN * intervalHours)}
              </p>
            )}
          </section>

          {/* Texts */}
          {kind === 'reviews' && (
            <section>
              <FieldLabel aside={<span className={textsShort ? 'text-warning' : 'text-success'}>{textList.length} / {totalN} texts</span>}>Review texts</FieldLabel>
              <textarea rows={8} className={`${INPUT} font-mono text-xs`} placeholder="One review per line" value={texts} onChange={(e) => setTexts(e.target.value)} />
              <div className="mt-2 p-3 rounded-lg bg-surface-muted space-y-2">
                <div className="text-xs font-medium text-content-muted">Write them with AI</div>
                <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-2">
                  <select className={INPUT} value={genLang} onChange={(e) => setGenLang(e.target.value)}>
                    <option value="">{countryInfo.get(countries[0] ?? '')?.language ? `${countryInfo.get(countries[0]!)!.language} (1st country)` : 'en'}</option>
                    {Array.from(new Set((overview.countries ?? []).map((c) => c.language))).sort().map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <input className={INPUT} placeholder="Notes: features to praise, words to avoid…" value={genNotes} onChange={(e) => setGenNotes(e.target.value)} />
                  <Button size="sm" variant="secondary" loading={generate.isPending} disabled={!pkg || totalN < 1} onClick={() => generate.mutate()}>
                    {textList.length && textList.length < totalN ? `Add ${totalN - textList.length}` : `Write ${totalN || ''}`}
                  </Button>
                </div>
              </div>
            </section>
          )}

          {/* Advanced */}
          <section className="border-t border-border pt-4">
            <button type="button" onClick={() => setAdvanced(!advanced)} className="text-sm font-medium text-content-muted hover:text-content">
              {advanced ? '▾' : '▸'} More options
            </button>
            {advanced && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <Toggle checked={highRisk} onChange={setHighRisk} label="High-risk app" hint="Gambling and other sensitive categories; ASO.Market may require it" />
                <Toggle checked={noGuarantee} onChange={setNoGuarantee} label="No guarantee" hint="Cheaper; removed reviews are not replaced" />
                <Toggle checked={screens} onChange={setScreens} label="Screenshot from inside the app" hint="Performer proves the app was opened" />
                {kind !== 'installs' && <Toggle checked={likes} onChange={setLikes} label="Likes on the review" />}
                {countries.length > 1 && (
                  <div className="py-1.5">
                    <FieldLabel aside={shareMin != null ? `${Math.ceil(shareMin * 100)}%–99%` : undefined}>Max share per country</FieldLabel>
                    <div className="flex items-center gap-2">
                      <input type="number" min={shareMin ?? 0} max={0.99} step={0.05} className={INPUT} placeholder="any" value={share} onChange={(e) => setShare(e.target.value)} />
                      <span className="text-xs text-content-subtle whitespace-nowrap">{share ? `${Math.round(Number(share) * 100)}%` : ''}</span>
                    </div>
                  </div>
                )}
                <div className="py-1.5">
                  <FieldLabel>Promo code</FieldLabel>
                  <input className={INPUT} value={promo} onChange={(e) => setPromo(e.target.value)} />
                </div>
              </div>
            )}
          </section>
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-0 self-start space-y-3">
          <div className="rounded-xl border border-border bg-surface-muted/60 p-4 space-y-3">
            <div className="text-xs uppercase tracking-wide text-content-subtle">Summary</div>
            <div className="space-y-1.5 text-sm">
              <Row label="App">{app?.name ?? (pkg || '—')}</Row>
              <Row label="Order">{kind === 'installs' ? 'Installs' : `${rating}★ ${kind === 'reviews' ? 'reviews' : 'ratings'}`}</Row>
              <Row label="Countries">{countries.length ? countries.map((c) => flag(c)).join(' ') : '—'}</Row>
              <Row label="Tasks">{totalN || '—'}</Row>
            </div>
            <div className="border-t border-border pt-3 space-y-1.5 text-sm">
              <Row label="Per task">{quote.isFetching ? '…' : q ? usd(q.price, q.price < 1 ? 3 : 2) : '—'}{q && q.percent > 0 ? <span className="ml-1 text-success">−{q.percent}%</span> : null}</Row>
              <div className="flex items-baseline justify-between">
                <span className="text-content-muted">Total</span>
                <span className="text-2xl font-semibold text-content tabular-nums">{quote.isFetching ? '…' : q ? usd(q.cost) : '—'}</span>
              </div>
              <Row label="Balance">{q ? usd(q.balance) : usd(overview.profile?.balance)}</Row>
              {q && <Row label="After order"><span className={q.balance - q.cost < 0 ? 'text-warning' : ''}>{usd(q.balance - q.cost)}</span></Row>}
            </div>
            {quote.isError && <Notice tone="danger">{(quote.error as Error).message}</Notice>}
            {!quotable && <p className="text-xs text-content-subtle">Pick the app, countries and size to see the price.</p>}
            <Button className="w-full" disabled={!canSubmit} loading={submit.isPending} onClick={() => submit.mutate()}>
              {q ? `Place order · ${usd(q.cost)}` : 'Place order'}
            </Button>
            {textsShort && quotable && <p className="text-xs text-warning">Add {totalN - textList.length} more review text{totalN - textList.length > 1 ? 's' : ''}.</p>}
          </div>
          {warnings.map((w, i) => <Notice key={i} tone={w.tone}>{w.text}</Notice>)}
          {overview.account.env === 'dev' && <Notice tone="info">Dev stand: test money, nothing reaches the real store.</Notice>}
        </aside>
      </div>
    </Modal>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-content-muted shrink-0">{label}</span>
      <span className="text-content text-right truncate">{children}</span>
    </div>
  );
}

export function useOrderModal() {
  const [state, setState] = useState<{ open: boolean; appId: string | null }>({ open: false, appId: null });
  const ref = useRef(setState);
  return {
    ...state,
    show: (appId: string | null = null) => ref.current({ open: true, appId }),
    hide: () => ref.current((s) => ({ ...s, open: false })),
  };
}
